import React, { useState } from 'react';
import { Plus, Calendar, Clock, Edit, Trash2 } from 'lucide-react';
import { Card, Button, Badge, Input } from '../../components/ui/index.jsx';
// Custom Modal component might be in ../../components/ui/Modal.jsx
import { Modal as UIModal } from '../../components/ui/Modal';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';


const AcademicYearList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const { data: yearsResponse, refresh, loading } = useGet(`/academic-years?page=${currentPage}`);
    
    // Extract years and pagination meta
    const years = yearsResponse?.data || [];
    const pagination = yearsResponse?.meta || null;
    const { hasPermission } = useAuth();
    const canCreate = hasPermission('academic-years', 'create');
    const canUpdate = hasPermission('academic-years', 'update');
    const canDelete = hasPermission('academic-years', 'delete');


    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        start_date: '',
        end_date: '',
    });


    const handleEdit = (year) => {
        setFormData({
            id: year.id,
            name: year.name,
            start_date: year.start_date ? year.start_date.split('T')[0] : '',
            end_date: year.end_date ? year.end_date.split('T')[0] : '',
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (formData.id) {
                await api.put(`/academic-years/${formData.id}`, formData);
            } else {
                await api.post('/academic-years', formData);
            }
            setIsModalOpen(false);
            setFormData({ name: '', start_date: '', end_date: '' });
            refresh();
        } catch (err) {
            alert('خطأ أثناء الحفظ');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if(confirm('هل أنت متأكد من حذف هذه السنة الدراسية؟')) {
            try {
                await api.delete(`/academic-years/${id}`);
                refresh();
            } catch (err) {
                alert('خطأ أثناء الحذف');
            }
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h2 className="text-xl  text-slate-800">السنوات الدراسية</h2>
                    <p className="text-slate-400 text-[11px] leading-none mt-1 ">إدارة المواعيد والسنوات الأكاديمية النشطة</p>
                </div>
                {canCreate && (
                    <Button onClick={() => { setFormData({ name: '', start_date: '', end_date: '' }); setIsModalOpen(true); }}>
                        <Plus className="w-5 h-5 text-white" /> إضافة سنة جديدة
                    </Button>
                )}
            </div>


            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {loading ? (
                    Array(3).fill(0).map((_, i) => <Card key={i} className="h-40 bg-slate-50 animate-pulse border-slate-100" />)
                ) : (Array.isArray(years) ? years : []).length === 0 ? (
                    <div className="col-span-full py-12 text-center text-slate-400  italic">لا توجد سنوات دراسية مسجلة حالياً</div>
                ) : (
                    years.map(year => (
                        <Card key={year.id} className="p-4 border-2 transition-all group hover:shadow-lg hover:-translate-y-0.5 duration-300 border-slate-100 shadow-sm">
                        <div className="flex justify-between items-start mb-4">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-slate-50 text-slate-400">
                                <Calendar size={24} />
                            </div>
                        </div>
                        <h4 className="text-xl  text-slate-800 mb-1">{year.name}</h4>
                        <div className="flex items-center gap-2 text-xs text-slate-400  mb-6 italic">
                            <Clock size={12} /> من {year.start_date?.split('T')[0]} إلى {year.end_date?.split('T')[0]}
                        </div>
                        
                        <div className="flex gap-2">
                             <Button 
                                variant="outline" 
                                className="flex-1 py-1.5 text-[11px]  uppercase tracking-wider"
                                onClick={() => {
                                    localStorage.setItem('selected_year', JSON.stringify(year));
                                    window.location.reload(); // Quick way to apply session change
                                }}
                            >
                                فتح هذه السنة
                            </Button>
                            {canUpdate && (
                                <button 
                                    onClick={() => handleEdit(year)}
                                    className="p-2 text-slate-300 hover:text-primary transition-colors border border-slate-100 rounded-lg hover:border-orange-100 bg-slate-50 hover:bg-orange-50 mr-2"
                                >
                                    <Edit size={16} />
                                </button>
                            )}
                            {canDelete && (
                                <button 
                                    onClick={() => handleDelete(year.id)}
                                    className="p-2 text-slate-300 hover:text-red-500 transition-colors border border-slate-100 rounded-lg hover:border-red-100 bg-slate-50 hover:bg-red-50"
                                >
                                    <Trash2 size={16} />
                                </button>
                            )}
                        </div>
                    </Card>
                    ))
                )}
            </div>

            {/* Pagination Controls */}
            {pagination && pagination.last_page > 1 && (
                <Card className="p-2 bg-white/50 backdrop-blur-sm border-slate-100 flex items-center justify-between">
                    <div className="px-4 text-xs text-slate-500">
                        عرض <span className="font-bold text-slate-700">{pagination.from}</span> إلى <span className="font-bold text-slate-700">{pagination.to}</span> من أصل <span className="font-bold text-slate-700">{pagination.total}</span> سنة
                    </div>
                    <div className="flex items-center gap-1">
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="h-8 px-3 text-xs"
                        >
                            السابق
                        </Button>
                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                let pageNum = i + 1;
                                if (pagination.last_page > 5 && currentPage > 3) {
                                    pageNum = currentPage - 3 + i + 1;
                                    if (pageNum > pagination.last_page) pageNum = pagination.last_page - (4 - i);
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`w-8 h-8 rounded-lg text-xs transition-all ${
                                            currentPage === pageNum 
                                            ? 'bg-primary text-white shadow-sm font-medium' 
                                            : 'text-slate-500 hover:bg-slate-100'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                            disabled={currentPage === pagination.last_page}
                            className="h-8 px-3 text-xs"
                        >
                            التالي
                        </Button>
                    </div>
                </Card>
            )}

            <UIModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={formData.id ? "تعديل السنة الدراسية" : "إضافة سنة دراسية جديدة"}>
                <form onSubmit={handleSubmit} className="space-y-4 pt-2">
                    <Input 
                        label="اسم السنة" 
                        value={formData.name} 
                        onChange={e => setFormData({...formData, name: e.target.value})} 
                        placeholder="مثال: 2025-2026" 
                        required 
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <Input 
                            label="تاريخ البدء" 
                            type="date" 
                            value={formData.start_date} 
                            onChange={e => setFormData({...formData, start_date: e.target.value})} 
                            required 
                        />
                        <Input 
                            label="تاريخ الانتهاء" 
                            type="date" 
                            value={formData.end_date} 
                            onChange={e => setFormData({...formData, end_date: e.target.value})} 
                            required 
                        />
                    </div>
                    <Button type="submit" isLoading={isSubmitting} className="w-full mt-4">
                        حفظ بيانات السنة الدراسية
                    </Button>
                </form>
            </UIModal>
        </div>
    );
};

export default AcademicYearList;
